import { useLayoutEffect, useMemo, useState } from 'react';
import { createFilter } from 'react-select';
import { FormComponentValue } from 'components/common/form/layout/control';
import useBundleTranslation from 'i18n';

export const ALL_TYPES = '__all__';

// react-select's default stringify searches `label + value`; the value here is an internal
// `type:id` key the author never sees, while the type labels they do see live only in props.
export const resourceSearchFilter = createFilter<FormComponentValue>({
    ignoreAccents: false,
    stringify: (option) =>
        [option.label, option.data.props?.type_label, option.data.props?.short_label].filter(Boolean).join(' '),
});

/** Map the backend's short_label onto the generic desc_right convention ReactSelectOption /
 *  ReactSelectSingleValue / ReactSelectMultiValue already render beside the icon + label. */
export function withShortLabelDesc(options: Array<FormComponentValue>): Array<FormComponentValue> {
    return options.map((opt) =>
        opt.props?.short_label ? { ...opt, props: { ...opt.props, desc_right: opt.props.short_label } } : opt,
    );
}

/**
 * The "Filter by type" dropdown + backing list shared by the Primary (single-select) and
 * Additional (multi-select) resource pickers. $selectedKeys always stay visible in the filtered
 * list regardless of the active type, so switching the filter never hides a current selection.
 * They are left out of the counts unless $countSelected — counts must match what the menu lists,
 * and only a multi-select hides its selected options from the menu.
 */
export function useResourceTypeFilter(
    options: Array<FormComponentValue>,
    selectedKeys: string[],
    countSelected = false,
) {
    const { t } = useBundleTranslation('app/editor/element/extreport');
    const [typeFilter, setTypeFilter] = useState(ALL_TYPES);

    const typeCounts = useMemo(() => {
        const counts = new Map<string, { label: string; count: number }>();
        options.forEach((opt) => {
            if (!countSelected && selectedKeys.includes(String(opt.value))) {
                return;
            }
            const logoKey = String(opt.props?.logo_key ?? '');
            if (!logoKey) {
                return;
            }
            const label = String(opt.props?.type_label ?? logoKey);
            counts.set(logoKey, { label, count: (counts.get(logoKey)?.count ?? 0) + 1 });
        });
        return counts;
    }, [options, selectedKeys, countSelected]);

    const typeFilterOptions: Array<FormComponentValue> = useMemo(
        () => [
            { value: ALL_TYPES, label: t('concierge_insight_filter_all_types') },
            ...Array.from(typeCounts.entries()).map(([logoKey, { label, count }]) => ({
                value: logoKey,
                label: `${label} (${count})`,
            })),
        ],
        [typeCounts, t],
    );

    // A filtered-to type with nothing left to pick (everything of that type got selected) —
    // fall back to All types instead of showing an empty menu tied to a vanished option.
    useLayoutEffect(() => {
        if (typeFilter !== ALL_TYPES && !typeCounts.has(typeFilter)) {
            setTypeFilter(ALL_TYPES);
        }
    }, [typeCounts, typeFilter]);

    const filteredOptions = useMemo(
        () =>
            typeFilter === ALL_TYPES
                ? options
                : options.filter(
                      (opt) => selectedKeys.includes(String(opt.value)) || opt.props?.logo_key === typeFilter,
                  ),
        [options, selectedKeys, typeFilter],
    );

    return { typeFilter, setTypeFilter, typeFilterOptions, filteredOptions };
}
